import { updateEntities } from '@redux/entities'
import { Schemas } from '@redux/schema'
import { normalize } from 'normalizr'

import api from '@functions/api'

import type {
  Action, EntityOptions, Errors, FetchRequestPayload, RootReducerState, UpdateParams,
} from '@redux/modules/types'

import { BrandThemeModel } from '@domain-group/realhub-backend-api-types'

import { AppDispatch } from '@redux/store'

import { handleError } from '../utils'

const FETCH_REQUEST = 'realbase/brandTheme/FETCH_REQUEST'
const FETCH_SUCCESS = 'realbase/brandTheme/FETCH_SUCCESS'
const FETCH_FAILURE = 'realbase/brandTheme/FETCH_FAILURE'

const CREATE_REQUEST = 'realbase/brandTheme/CREATE_REQUEST'
const CREATE_SUCCESS = 'realbase/brandTheme/CREATE_SUCCESS'
const CREATE_FAILURE = 'realbase/brandTheme/CREATE_FAILURE'

const UPDATE_REQUEST = 'realbase/brandTheme/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realbase/brandTheme/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realbase/brandTheme/UPDATE_FAILURE'

export type BrandThemeModulestate = {
  creating: boolean,
  errors: Errors,
  loaded: boolean,
  loadedForKeys: string[],
  loading: boolean,
  updating: boolean,
}

// Initial State
const initialState: BrandThemeModulestate = {
  creating: false,
  errors: [],
  loaded: false,
  loadedForKeys: [],
  loading: false,
  updating: false,
}

// Actions
export function fetchRequest(payload: FetchRequestPayload = {}): Action {
  return {
    type: FETCH_REQUEST,
    loadedForKeys: payload.loadedForKeys,
  }
}

export function fetchSuccess(): Action {
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors : Errors = []): Action {
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest(): Action {
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess(): Action {
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors: Errors = []): Action {
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest(): Action {
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess(): Action {
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors : Errors = []): Action {
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

// Action Creators
export function loadBrandThemes(options: EntityOptions = {}) {
  const { entityKey } = options || {}

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const loadedForKeys = [...getState().brandThemes.loadedForKeys]
    if (entityKey && !loadedForKeys.includes(entityKey)){
      loadedForKeys.push(entityKey)
    }

    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api('/brand_themes.json', options)
      .then(({ data }: { data: BrandThemeModel[] }) => {
        const normalizedJson = normalize(data, Schemas.BRAND_THEME_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function createBrandTheme(brandTheme: BrandThemeModel, options: EntityOptions = {}) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      brand_theme: brandTheme,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api('/brand_themes.json', options, config)
      .then(({ data }: { data: BrandThemeModel }) => {
        const normalizedJson = normalize(data, Schemas.BRAND_THEME)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function updateBrandTheme(
  brandTheme: UpdateParams<BrandThemeModel>,
  options: EntityOptions = {},
) {
  const config = {
    method: 'PUT',
    data: JSON.stringify({
      brand_theme: brandTheme,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/brand_themes/${brandTheme.id}.json`, options, config)
      .then(({ data }: { data: BrandThemeModel }) => {
        const normalizedJson = normalize(data, Schemas.BRAND_THEME)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

// Reducer
export default function reducer(
  state: BrandThemeModulestate = initialState,
  action: Action = { type: '' },
) {
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loadedForKeys: action.loadedForKeys || state.loadedForKeys,
        loading: true,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors,
      }
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors,
      }
    case UPDATE_REQUEST:
      return {
        ...state,
        updating: true,
      }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors: action.errors,
      }
    default:
      return state
  }
}
